// Pure rules for the one-time wallet top-up card. The server decides everything
// again (WalletTopUps.java); these keep the form honest and the words consistent.
import type { TopUpInvoiceStatus, TopUpQuote, TopUpState } from '../../api/services/walletTopups.service';
import { roleIn } from '../../utils/roleGrants';
import { formatServerUtcAsIst } from './closeBooksRules';

/**
 * Who may CREATE a payable link: owners and the organisations above them (owner
 * decision, 2026-10-25). A business's admins (OPERATOR_ADMIN = BUSINESS_ADMIN)
 * are deliberately absent: they read the quote and the history only.
 */
export const TOPUP_CREATE_ROLES = [
  'platform_admin', 'master_admin', 'super_admin', 'provider_owner', 'service_admin', 'provider_admin',
  'business_owner',
];

/** Who may read the quote and the history. */
export const TOPUP_READ_ROLES = [...TOPUP_CREATE_ROLES, 'operator_admin', 'business_admin'];

export const canCreateTopUp = (role: string | null | undefined): boolean => roleIn(role, TOPUP_CREATE_ROLES);
export const canReadTopUps = (role: string | null | undefined): boolean => roleIn(role, TOPUP_READ_ROLES);

/** The server's flag is off: shown as a quiet "not available yet", never as an error. */
export const isTopUpDisabled = (q: TopUpQuote | null | undefined): boolean =>
  !!q && q.refusal !== null && q.refusal !== undefined && q.refusalStatus === 409
  && /not enabled/i.test(q.refusal);

/** A refusal that is about the amount typed (bounds, decimals), shown beside the input. */
export const isAmountRefusal = (q: TopUpQuote | null | undefined): boolean =>
  !!q && !!q.refusal && q.refusalStatus === 400;

const DEFAULTS: Record<string, number> = {
  INR: 1000, USD: 20, EUR: 20, GBP: 20, SGD: 25, AUD: 30, CAD: 30, AED: 100,
};

/** A sensible first amount for the currency, inside the server's bounds when they are known. */
export const defaultAmount = (currency?: string | null, min?: number | null, max?: number | null): number => {
  let v = DEFAULTS[(currency || 'INR').toUpperCase()] ?? 100;
  if (typeof min === 'number' && v < min) v = min;
  if (typeof max === 'number' && v > max) v = max;
  return v;
};

/** What is wrong with the typed amount, or null. Bounds are checked only when known. */
export const amountError = (raw: string, min?: number | null, max?: number | null): string | null => {
  const t = raw.trim();
  if (!t) return 'Enter an amount';
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return 'Use a positive amount with at most two decimals';
  const n = Number(t);
  if (!(n > 0)) return 'Use a positive amount';
  if (typeof min === 'number' && n < min) return `The minimum is ${min}`;
  if (typeof max === 'number' && n > max) return `The maximum is ${max}`;
  return null;
};

export const TOPUP_STATE_LABELS: Record<TopUpState, string> = {
  PENDING: 'Creating link',
  OPEN: 'Awaiting payment',
  PAID: 'Paid · credited',
  EXPIRED: 'Expired',
  LINK_FAILED: 'Link failed',
  REVIEW: 'Under review',
};

export const topUpStateClass = (s: TopUpState): string =>
  s === 'PAID'
    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    : s === 'OPEN' || s === 'PENDING'
      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      : s === 'LINK_FAILED' || s === 'REVIEW'
        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';

export const awaitingPayment = (s: TopUpState | null | undefined): boolean => s === 'PENDING' || s === 'OPEN';

export const INVOICE_STATUS_LABELS: Record<TopUpInvoiceStatus, string> = {
  NOT_INVOICED: 'Not invoiced yet',
  RECEIPTED: 'Receipt issued',
  TAX_INVOICED: 'Tax invoice issued',
};

export const invoiceLabel = (status?: TopUpInvoiceStatus | null, invoiceNumber?: string | null,
  taxDocumentNumber?: string | null): string => {
  const base = status ? INVOICE_STATUS_LABELS[status] || status : '-';
  const nums = [invoiceNumber, taxDocumentNumber].filter((x): x is string => !!x && x.trim() !== '');
  return nums.length ? `${base} · ${nums.join(' · ')}` : base;
};

/** A server time, as epoch millis or a zone-less UTC string, in IST. */
export const formatTopUpTime = (v?: string | number | null): string => {
  if (v === null || v === undefined || v === '') return '-';
  if (typeof v === 'number') {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  }
  return formatServerUtcAsIst(v);
};

/** The tax line's caption: the treatment and rate, e.g. "GST 18%". */
const TREATMENTS: Record<string, string> = {
  GST_INTRA: 'GST (CGST + SGST)',
  GST_INTER: 'GST (IGST)',
  EXPORT_LUT: 'No GST (export under LUT)',
};

export const taxCaption = (q: Pick<TopUpQuote, 'taxTreatment' | 'taxRate'>): string => {
  const key = q.taxTreatment || '';
  const t = TREATMENTS[key] || (key ? key.replace(/_/g, ' ') : 'Tax');
  return typeof q.taxRate === 'number' && q.taxRate > 0 ? `${t} ${q.taxRate}%` : t;
};
